import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Flex, SimpleGrid, Icon, Text,
  useColorModeValue, Card, CardBody, Badge, Spinner, Center,
  HStack, VStack, Divider, Input, useToast,
  Table, Thead, Tbody, Tr, Th, Td, TableContainer,
  Step, StepIcon, StepIndicator, StepNumber,
  Stepper, StepSeparator, StepStatus, StepTitle,
  Alert, AlertIcon, AlertDescription, Radio, RadioGroup, Stack as ChakraStack,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton, ModalFooter,
  useDisclosure,
} from '@chakra-ui/react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  MdArrowBack, MdArrowForward, MdUploadFile, MdPictureAsPdf, MdCode,
  MdCheckCircle, MdAdd, MdSearch, MdVisibility, MdReceiptLong,
  MdDescription, MdLink, MdBlock, MdWarning,
} from 'react-icons/md';
import InputField from 'components/fields/InputField';
import PickList from 'components/fields/PickList';
import SelectField from 'components/fields/SelectField';
import facturasApi from 'api/facturas';
import proveedoresApi from 'api/proveedores';
import lineasApi from 'api/lineas';
import familiasApi from 'api/familias';
import marcasApi from 'api/marcas';
import modelosApi from 'api/modelos';
import departamentosApi from 'api/departamentos';
import tipoAdquisicionApi from 'api/tipoAdquisicion';
import arrendadorasApi from 'api/arrendadoras';
import depreciacionApi from 'api/depreciacion';
import { getDepartamentosPermitidos } from 'utils/permissions';
import { hotelMap } from 'views/admin/salidas/types';
import { VistaActual, TipoCarga } from 'views/admin/facturas/types';

// ─── Helpers ───────────────────────────────────────────────────────────────

const formatMoneda = (valor: any, moneda = 'MXN') => {
  const num = Number(valor);
  if (Number.isNaN(num)) return '—';
  try {
    return num.toLocaleString('es-MX', { style: 'currency', currency: moneda || 'MXN' });
  } catch {
    return `$${num.toLocaleString('es-MX')}`;
  }
};

const formatFecha = (fecha: any) => {
  if (!fecha) return '—';
  try {
    return new Date(fecha).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return String(fecha);
  }
};

const getEstatusFacturaColor = (estatus: string) => {
  switch (estatus) {
    case 'registrada': return 'green';
    case 'cancelada': return 'red';
    default: return 'gray';
  }
};

const getEstatusRelacionColor = (estatus: string) => {
  switch (estatus) {
    case 'nuevo_activo': return 'blue';
    case 'activo_existente': return 'purple';
    case 'omitido': return 'gray';
    default: return 'orange';
  }
};

const getEstatusRelacionLabel = (estatus: string) => {
  switch (estatus) {
    case 'nuevo_activo': return 'Nuevo activo';
    case 'activo_existente': return 'Activo existente';
    case 'omitido': return 'Omitido';
    default: return 'Pendiente';
  }
};

// ─── Selector de archivo (input real + botón, mismo patrón que newProduct) ──

const FileFieldPicker = ({
  label, accept, file, onFileSelected, onClear, required, borderColor, hint,
}: {
  label: string; accept: string; file: File | null;
  onFileSelected: (file: File) => void; onClear?: () => void;
  required?: boolean; borderColor: string; hint?: string;
}) => {
  const inputId = `file-picker-${label.replace(/\s+/g, '-').toLowerCase()}`;
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const brand = useColorModeValue('blue.500', 'blue.300');
  return (
    <Box>
      <Flex align="center" mb={2}>
        <Text fontSize="sm" fontWeight="600">{label}</Text>
        {required && <Text color="red.500" ml="2px">*</Text>}
      </Flex>
      {!file ? (
        <Box
          border="2px dashed" borderColor={borderColor} borderRadius="lg"
          p={5} textAlign="center" cursor="pointer" _hover={{ bg: hoverBg }}
          onClick={() => document.getElementById(inputId)?.click()}
        >
          <Icon as={MdUploadFile} w="32px" h="32px" color={brand} mb={2} />
          <Text fontSize="sm" color="gray.500">Haz clic para seleccionar el archivo</Text>
          {hint && <Text fontSize="xs" color="gray.400" mt={1}>{hint}</Text>}
          <input
            id={inputId} type="file" accept={accept} style={{ display: 'none' }}
            onChange={e => {
              const f = e.target.files?.[0];
              e.target.value = '';
              if (f) onFileSelected(f);
            }}
          />
        </Box>
      ) : (
        <Flex align="center" justify="space-between" p={3} border="1px solid" borderColor={borderColor} borderRadius="lg">
          <HStack spacing={2} overflow="hidden">
            <Icon as={MdDescription} color={brand} flexShrink={0} />
            <Text fontSize="sm" isTruncated>{file.name}</Text>
            <Text fontSize="xs" color="gray.400" flexShrink={0}>({(file.size / 1024).toFixed(1)} KB)</Text>
          </HStack>
          <Button size="xs" variant="ghost" colorScheme="red" onClick={() => onClear?.()}>
            Quitar
          </Button>
        </Flex>
      )}
    </Box>
  );
};

// ─── Componente ──────────────────────────────────────────────────────────────

const FacturasPage = () => {
  const toast = useToast();
  const textPrimary = useColorModeValue('gray.800', 'white');
  const textSecondary = useColorModeValue('gray.500', 'gray.400');
  const cardBg = useColorModeValue('white', 'navy.700');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
  const sectionBg = useColorModeValue('gray.50', 'navy.700');
  const labelStyle = { fontSize: 'xs', fontWeight: 700 as any, color: textSecondary, textTransform: 'uppercase' as const, letterSpacing: '0.05em' };

  const usuario = useMemo(() => {
    try { return JSON.parse(sessionStorage.getItem('user') || '{}'); } catch { return {}; }
  }, []);
  const [hotelSeleccionado] = useState(sessionStorage.getItem('hotelSeleccionado') || '');
  const hotelId = hotelMap[hotelSeleccionado] || null;

  const { isOpen: isVerOpen, onOpen: onOpenVer, onClose: onCloseVer } = useDisclosure();
  const [facturaViendo, setFacturaViendo] = useState<any>(null);
  const [loadingVer, setLoadingVer] = useState(false);

  const [vistaActual, setVistaActual] = useState<VistaActual>('lista');
  const [tipoCarga, setTipoCarga] = useState<TipoCarga | null>(null);
  const [pasoActual, setPasoActual] = useState(0);

  const [facturas, setFacturas] = useState<any[]>([]);
  const [loadingLista, setLoadingLista] = useState(true);

  // ── Catálogos compartidos ──
  const [proveedores, setProveedores] = useState<any[]>([]);
  const [lineas, setLineas] = useState<any[]>([]);
  const [familias, setFamilias] = useState<any[]>([]);
  const [marcas, setMarcas] = useState<any[]>([]);
  const [modelos, setModelos] = useState<any[]>([]);
  const [departamentosCatalogo, setDepartamentosCatalogo] = useState<any[]>([]);
  const [tiposAdquisicion, setTiposAdquisicion] = useState<any[]>([]);
  const [arrendadoras, setArrendadoras] = useState<any[]>([]);
  const [isCreatingArrendadora, setIsCreatingArrendadora] = useState(false);
  const [rubrosDepreciacion, setRubrosDepreciacion] = useState<any[]>([]);

  // ── Flujo XML ──
  const [xmlFile, setXmlFile] = useState<File | null>(null);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<any>(null);
  const [parseError, setParseError] = useState<{ error: string; reason?: string } | null>(null);
  const [parsing, setParsing] = useState(false);
  const [conceptosEditable, setConceptosEditable] = useState<any[]>([]);
  const [guardandoFactura, setGuardandoFactura] = useState(false);
  const [facturaGuardada, setFacturaGuardada] = useState<{ facturaId: number; folio: string; conceptos: any[] } | null>(null);

  // ── Flujo manual ──
  const [manualForm, setManualForm] = useState({
    folio: '', fecha: new Date().toISOString().split('T')[0], proveedorId: '',
  });
  const [manualPdfFile, setManualPdfFile] = useState<File | null>(null);
  const [guardandoManual, setGuardandoManual] = useState(false);

  // ── Relacionar artículos ──
  const [conceptoEnProceso, setConceptoEnProceso] = useState<number | null>(null);
  const [modoRelacion, setModoRelacion] = useState<'nuevo' | 'existente' | null>(null);
  const [busquedaTipo, setBusquedaTipo] = useState<'serial' | 'marca_modelo'>('serial');
  const [busquedaSerial, setBusquedaSerial] = useState('');
  const [busquedaMarcaId, setBusquedaMarcaId] = useState('');
  const [busquedaModeloId, setBusquedaModeloId] = useState('');
  const [resultadosBusqueda, setResultadosBusqueda] = useState<any[]>([]);
  const [buscando, setBuscando] = useState(false);
  const [busquedaRealizada, setBusquedaRealizada] = useState(false);
  const [nuevoActivoForm, setNuevoActivoForm] = useState<any>({});
  // Un número de serie por cada unidad que trae la línea de factura (según su
  // Cantidad) — se captura uno por uno en vez de un solo SerialNumber general.
  const [seriesNuevoActivo, setSeriesNuevoActivo] = useState<string[]>(['']);
  const [nuevoActivoImagen, setNuevoActivoImagen] = useState<File | null>(null);
  const [guardandoRelacion, setGuardandoRelacion] = useState(false);

  const pasosXml = ['Datos', 'Confirmar', 'Relacionar artículos', 'Resumen'];

  // ── Carga inicial ────────────────────────────────────────────────────────

  // Hoteles a los que el usuario tiene acceso (se usa cuando no hay ninguno
  // elegido en el nav) y el hotel elegido en el nav, resuelto de inmediato y
  // por polling (el nav no dispara ningún evento al cambiar de hotel).
  const todosMisHotelIds = useMemo(() => {
    const raw = usuario.HotelIds;
    if (!raw) return [] as number[];
    return String(raw).split(',').map((s: string) => parseInt(s.trim(), 10)).filter((n: number) => !isNaN(n));
  }, [usuario]);

  const [hotelSeleccionadoId, setHotelSeleccionadoId] = useState<number | null>(hotelId);

  useEffect(() => {
    const resolverHotelSeleccionado = () => {
      const actual = hotelMap[sessionStorage.getItem('hotelSeleccionado') || ''] || null;
      setHotelSeleccionadoId(prev => (prev !== actual ? actual : prev));
    };
    resolverHotelSeleccionado();
    const interval = setInterval(resolverHotelSeleccionado, 1000);
    return () => clearInterval(interval);
  }, []);

  const hotelIdsFiltro = hotelSeleccionadoId ? [hotelSeleccionadoId] : todosMisHotelIds;

  const cargarFacturas = async () => {
    setLoadingLista(true);
    try {
      const data = await facturasApi.listado(hotelIdsFiltro);
      setFacturas(Array.isArray(data) ? data : []);
    } catch {
      setFacturas([]);
    } finally {
      setLoadingLista(false);
    }
  };

  useEffect(() => { cargarFacturas(); }, [hotelSeleccionadoId]);

  useEffect(() => {
    proveedoresApi.listado().then((d: any) => setProveedores(Array.isArray(d) ? d : [])).catch(() => setProveedores([]));
    lineasApi.listado().then((d: any) => setLineas(Array.isArray(d) ? d : [])).catch(() => setLineas([]));
    familiasApi.listado().then((d: any) => setFamilias(Array.isArray(d) ? d : [])).catch(() => setFamilias([]));
    marcasApi.listado().then((d: any) => setMarcas(Array.isArray(d) ? d : [])).catch(() => setMarcas([]));
    modelosApi.listado().then((d: any) => setModelos(Array.isArray(d) ? d : [])).catch(() => setModelos([]));
    tipoAdquisicionApi.listado().then((d: any) => setTiposAdquisicion(Array.isArray(d) ? d : [])).catch(() => setTiposAdquisicion([]));
    arrendadorasApi.listado().then((d: any) => setArrendadoras(Array.isArray(d) ? d : [])).catch(() => setArrendadoras([]));
    departamentosApi.listado().then((d: any) => {
      const permitidos = getDepartamentosPermitidos();
      const lista = Array.isArray(d) ? d : [];
      setDepartamentosCatalogo(permitidos ? lista.filter((dep: any) => permitidos.includes(Number(dep.Id))) : lista);
    }).catch(() => setDepartamentosCatalogo([]));
  }, []);

  // Rubro de depreciación: catálogo del año en que se adquirió el activo nuevo
  // (igual que en el alta normal de activos), recalculado si cambia la fecha.
  useEffect(() => {
    const anio = nuevoActivoForm.FechaAdquisicion
      ? Number(nuevoActivoForm.FechaAdquisicion.split('-')[0])
      : new Date().getFullYear();
    depreciacionApi.listado(anio).then((d: any) => setRubrosDepreciacion(Array.isArray(d) ? d : [])).catch(() => setRubrosDepreciacion([]));
  }, [nuevoActivoForm.FechaAdquisicion]);

  // ── Helpers UI ────────────────────────────────────────────────────────────

  const mostrarError = (msg: string) =>
    toast({ title: 'Error', description: msg, status: 'error', duration: 5000, isClosable: true });
  const mostrarExito = (msg: string) =>
    toast({ title: 'Éxito', description: msg, status: 'success', duration: 5000, isClosable: true });

  const handleVolverALista = () => {
    setVistaActual('lista');
    setTipoCarga(null);
    setPasoActual(0);
    setXmlFile(null);
    setPdfFile(null);
    setParsedData(null);
    setParseError(null);
    setConceptosEditable([]);
    setFacturaGuardada(null);
    setManualForm({ folio: '', fecha: new Date().toISOString().split('T')[0], proveedorId: '' });
    setManualPdfFile(null);
    cerrarSubformRelacion();
    cargarFacturas();
  };

  const cerrarSubformRelacion = () => {
    setConceptoEnProceso(null);
    setModoRelacion(null);
    setBusquedaSerial('');
    setBusquedaMarcaId('');
    setBusquedaModeloId('');
    setResultadosBusqueda([]);
    setBusquedaRealizada(false);
    setNuevoActivoForm({});
    setSeriesNuevoActivo(['']);
    setNuevoActivoImagen(null);
  };

  const actualizarConceptoLocal = (conceptoId: number, cambios: any) => {
    setFacturaGuardada(prev => prev ? {
      ...prev,
      conceptos: prev.conceptos.map((c: any) => c.Id === conceptoId ? { ...c, ...cambios } : c),
    } : prev);
  };

  // ── Ver factura (modal) ──────────────────────────────────────────────────

  const handleVerFactura = async (factura: any) => {
    onOpenVer();
    setLoadingVer(true);
    try {
      const detalle = await facturasApi.detalle(factura.Id);
      setFacturaViendo(detalle?.factura ? { ...detalle.factura, conceptos: detalle.conceptos } : factura);
    } catch {
      setFacturaViendo(factura);
    } finally {
      setLoadingVer(false);
    }
  };

  const [cancelandoFactura, setCancelandoFactura] = useState(false);

  const handleCancelarFactura = async () => {
    if (!facturaViendo) return;
    if (!window.confirm(`¿Seguro que quieres cancelar la factura ${facturaViendo.Folio}? Esta acción no se puede deshacer.`)) {
      return;
    }
    setCancelandoFactura(true);
    try {
      const res = await facturasApi.cancelar(facturaViendo.Id);
      if (!res || res.success === false) {
        mostrarError(res?.error || 'No se pudo cancelar la factura');
        return;
      }
      setFacturaViendo((prev: any) => prev && { ...prev, Estatus: 'cancelada' });
      setFacturas((prev) => prev.map((f: any) => f.Id === facturaViendo.Id ? { ...f, Estatus: 'cancelada' } : f));
      mostrarExito('Factura cancelada');
    } finally {
      setCancelandoFactura(false);
    }
  };

  // ── Flujo XML: paso 0 ────────────────────────────────────────────────────

  const handleXmlSeleccionado = async (file: File) => {
    setXmlFile(file);
    setParsing(true);
    setParseError(null);
    setParsedData(null);
    try {
      const res = await facturasApi.parseXml(file);
      if (res && res.error) {
        setParseError(res);
        return;
      }
      setParsedData(res);
      setConceptosEditable((res.conceptos || []).map((c: any, i: number) => ({ ...c, _uid: i })));
    } catch {
      setParseError({ error: 'No se pudo leer el archivo XML' });
    } finally {
      setParsing(false);
    }
  };

  const handleAvanzarDatosXml = () => {
    if (!parsedData) { mostrarError('Primero carga un XML válido'); return; }
    if (!pdfFile) { mostrarError('Adjunta el PDF de la factura'); return; }
    setPasoActual(1);
  };

  // ── Flujo XML: paso 1 (confirmar) ────────────────────────────────────────

  const handleConceptoEditableChange = (index: number, field: string, value: string) => {
    setConceptosEditable(prev => prev.map((c, i) => i === index ? { ...c, [field]: value } : c));
  };

  const handleGuardarFacturaXml = async () => {
    if (!parsedData) return;
    setGuardandoFactura(true);
    try {
      const payload = {
        ...parsedData,
        conceptos: conceptosEditable,
        hotelId: hotelSeleccionadoId,
        usuarioCreadorId: usuario.Id,
      };
      const res = await facturasApi.importar(pdfFile, payload);
      if (!res || !res.facturaId) throw new Error('respuesta inválida');
      setFacturaGuardada({
        facturaId: res.facturaId,
        folio: res.folio || parsedData.folio,
        conceptos: res.conceptos || conceptosEditable.map((c, i) => ({ ...c, Id: i, EstatusRelacion: 'pendiente' })),
      });
      mostrarExito(`Factura ${res.folio || ''} guardada correctamente`);
      setPasoActual(2);
    } catch {
      mostrarError('No se pudo guardar la factura. Verifica los datos e intenta de nuevo.');
    } finally {
      setGuardandoFactura(false);
    }
  };

  // ── Flujo XML: paso 2 (relacionar artículos) ─────────────────────────────

  const familiasFiltradas = useMemo(() => {
    if (!nuevoActivoForm.LineaId) return [];
    return familias.filter((f: any) => f.LineaId && f.LineaId.toString() === nuevoActivoForm.LineaId.toString());
  }, [familias, nuevoActivoForm.LineaId]);

  const tipoEsArrendamiento = useMemo(() => {
    const tipo = tiposAdquisicion.find((t: any) => t.Id.toString() === nuevoActivoForm.TipoAdquisicionId?.toString());
    return tipo?.Name?.toLowerCase() === 'arrendamiento';
  }, [tiposAdquisicion, nuevoActivoForm.TipoAdquisicionId]);

  // Folio previsto (solo informativo): se recalcula de forma atómica al guardar,
  // esto es nada más para que se vea antes de crear el activo.
  const [folioPreview, setFolioPreview] = useState<string | null>(null);
  const [folioPreviewError, setFolioPreviewError] = useState<string | null>(null);

  // Dado el folio previsto de la primera unidad, calcula el de la última
  // (solo para mostrar el rango completo cuando hay varias unidades).
  const calcularUltimoFolioPreview = (primerFolio: string, cantidad: number): string => {
    const partes = primerFolio.split('-');
    if (partes.length !== 3) return primerFolio;
    const consecutivo = parseInt(partes[2], 10) + (cantidad - 1);
    return `${partes[0]}-${partes[1]}-${consecutivo.toString().padStart(partes[2].length, '0')}`;
  };

  useEffect(() => {
    if (!nuevoActivoForm.FamiliaId || !hotelSeleccionadoId) {
      setFolioPreview(null);
      setFolioPreviewError(null);
      return;
    }
    let cancelado = false;
    fetch('https://inventariosbackend.velasresorts.com/inventarios/getConsecutivo', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ FamiliaId: nuevoActivoForm.FamiliaId, HotelId: hotelSeleccionadoId }),
    })
      .then(r => r.json())
      .then(d => {
        if (cancelado) return;
        if (d?.folio?.success && d?.folio?.folio) {
          setFolioPreview(d.folio.folio);
          setFolioPreviewError(null);
        } else {
          setFolioPreview(null);
          setFolioPreviewError(d?.folio?.error || 'No se pudo calcular el folio');
        }
      })
      .catch(() => {
        if (!cancelado) { setFolioPreview(null); setFolioPreviewError('No se pudo calcular el folio'); }
      });
    return () => { cancelado = true; };
  }, [nuevoActivoForm.FamiliaId, hotelSeleccionadoId]);

  const handleCreateArrendadora = async (nombre: string) => {
    const nombreLimpio = nombre.trim();
    if (!nombreLimpio) return;
    setIsCreatingArrendadora(true);
    try {
      const nueva = await arrendadorasApi.crear(nombreLimpio);
      setArrendadoras((prev: any[]) => [...prev, nueva]);
      setNuevoActivoForm((p: any) => ({ ...p, ArrendadoraId: nueva.Id.toString() }));
      mostrarExito(`"${nueva.Name}" se agregó al catálogo de arrendadoras.`);
    } catch {
      mostrarError('No se pudo crear la arrendadora.');
    } finally {
      setIsCreatingArrendadora(false);
    }
  };

  const handleAbrirNuevoActivo = (concepto: any) => {
    setConceptoEnProceso(concepto.Id);
    setModoRelacion('nuevo');
    setNuevoActivoForm({
      ActiveName: concepto.Descripcion || '',
      Precio: concepto.Importe != null ? String(concepto.Importe) : '',
      LineaId: '', FamiliaId: '', MarcaId: '', ModeloId: '',
      DepartamentoId: usuario.DepartamentoId ? String(usuario.DepartamentoId) : '',
      TipoAdquisicionId: '', ArrendadoraId: '', CategoriaDepreciacionId: '',
      FechaAdquisicion: '', Garantia: '', UbicacionExacta: '',
    });
    // La factura trae la cantidad real de unidades de esta línea (ej. "5
    // laptops" en un solo renglón) — se pide un número de serie POR UNIDAD,
    // en vez de limitarse a crear un solo activo sin importar la cantidad.
    const unidades = Math.max(1, Math.round(Number(concepto.Cantidad) || 1));
    setSeriesNuevoActivo(Array.from({ length: unidades }, () => ''));
    setNuevoActivoImagen(null);
  };

  const handleAbrirExistente = (concepto: any) => {
    setConceptoEnProceso(concepto.Id);
    setModoRelacion('existente');
    setBusquedaSerial('');
    setBusquedaMarcaId('');
    setBusquedaModeloId('');
    setResultadosBusqueda([]);
    setBusquedaRealizada(false);
  };

  const handleOmitirConcepto = (concepto: any) => {
    actualizarConceptoLocal(concepto.Id, { EstatusRelacion: 'omitido' });
    if (conceptoEnProceso === concepto.Id) cerrarSubformRelacion();
  };

  const handleBuscarActivos = async () => {
    if (busquedaTipo === 'serial' && !busquedaSerial.trim()) {
      mostrarError('Ingresa el número de serie a buscar'); return;
    }
    if (busquedaTipo === 'marca_modelo' && !busquedaMarcaId && !busquedaModeloId) {
      mostrarError('Selecciona marca y/o modelo para buscar'); return;
    }
    setBuscando(true);
    setBusquedaRealizada(true);
    try {
      const params: any = { hotelId: hotelSeleccionadoId };
      if (busquedaTipo === 'serial') {
        params.serial = busquedaSerial.trim();
      } else {
        if (busquedaMarcaId) params.marcaId = busquedaMarcaId;
        if (busquedaModeloId) params.modeloId = busquedaModeloId;
      }
      const res = await facturasApi.buscarActivosParaRelacionar(params);
      setResultadosBusqueda(Array.isArray(res) ? res : []);
    } catch {
      mostrarError('Error al buscar activos');
      setResultadosBusqueda([]);
    } finally {
      setBuscando(false);
    }
  };

  const handleVincularExistente = async (concepto: any, activo: any) => {
    if (!facturaGuardada) return;
    setGuardandoRelacion(true);
    try {
      await facturasApi.relacionarActivoExistente(facturaGuardada.facturaId, concepto.Id, activo.Id);
      actualizarConceptoLocal(concepto.Id, { EstatusRelacion: 'activo_existente', ActivoId: activo.Id });
      mostrarExito(`Concepto vinculado al activo ${activo.Folio || activo.Id}`);
      cerrarSubformRelacion();
    } catch {
      mostrarError('No se pudo vincular el activo existente');
    } finally {
      setGuardandoRelacion(false);
    }
  };

  const handleNuevoActivoChange = (id: string, value: string) => {
    setNuevoActivoForm((prev: any) => {
      const next = { ...prev, [id]: value };
      if (id === 'LineaId') next.FamiliaId = '';
      return next;
    });
  };

  const handleGuardarNuevoActivo = async (concepto: any) => {
    if (!facturaGuardada) return;
    const faltantes: string[] = [];
    if (!nuevoActivoForm.LineaId) faltantes.push('Línea');
    if (!nuevoActivoForm.FamiliaId) faltantes.push('Sublínea');
    if (!nuevoActivoForm.MarcaId) faltantes.push('Marca');
    if (!nuevoActivoForm.ModeloId) faltantes.push('Modelo');
    if (!nuevoActivoForm.DepartamentoId) faltantes.push('Departamento');
    if (!nuevoActivoForm.TipoAdquisicionId) faltantes.push('Tipo de adquisición');
    if (faltantes.length > 0) {
      mostrarError(`Falta completar: ${faltantes.join(', ')}`);
      return;
    }
    if (tipoEsArrendamiento && !nuevoActivoForm.ArrendadoraId) {
      mostrarError('Selecciona la arrendadora');
      return;
    }
    const seriesLimpias = seriesNuevoActivo.map(s => s.trim());
    if (seriesLimpias.some(s => !s)) {
      mostrarError(
        seriesLimpias.length > 1
          ? `Completa el número de serie de las ${seriesLimpias.length} unidades`
          : 'Falta completar: Número de serie'
      );
      return;
    }
    if (new Set(seriesLimpias.map(s => s.toUpperCase())).size !== seriesLimpias.length) {
      mostrarError('Hay números de serie repetidos entre las unidades');
      return;
    }
    setGuardandoRelacion(true);
    try {
      // El folio de cada unidad (uno por cada número de serie) se genera y
      // reserva atómicamente en el backend — aquí solo se mandan los datos
      // compartidos del lote + la lista de series.
      const payload = {
        ActiveName: nuevoActivoForm.ActiveName || concepto.Descripcion,
        Precio: nuevoActivoForm.Precio,
        LineaId: nuevoActivoForm.LineaId,
        FamiliaId: nuevoActivoForm.FamiliaId,
        MarcaId: nuevoActivoForm.MarcaId,
        ModeloId: nuevoActivoForm.ModeloId,
        Series: seriesLimpias,
        Descripcion: concepto.Descripcion,
        HotelId: hotelSeleccionadoId,
        UsuariosId: usuario.Id,
        EstadosId: '1', // Activo/disponible por default; este subformulario no lo deja elegir
        DepartamentoId: nuevoActivoForm.DepartamentoId,
        TipoAdquisicionId: nuevoActivoForm.TipoAdquisicionId,
        TieneArrendamiento: tipoEsArrendamiento,
        ArrendadoraId: tipoEsArrendamiento ? nuevoActivoForm.ArrendadoraId : null,
        CategoriaDepreciacionId: nuevoActivoForm.CategoriaDepreciacionId || null,
        FechaAdquisicion: nuevoActivoForm.FechaAdquisicion || null,
        UbicacionExacta: nuevoActivoForm.UbicacionExacta || null,
        Garantia: nuevoActivoForm.Garantia || null,
        PermitidoSalir: null as boolean | null,
      };
      const res = await facturasApi.relacionarNuevoActivo(facturaGuardada.facturaId, concepto.Id, payload, nuevoActivoImagen);
      if (!res || res.success === false) throw new Error(res?.error || 'respuesta inválida');
      actualizarConceptoLocal(concepto.Id, {
        EstatusRelacion: 'nuevo_activo',
        ActivoId: res.activos?.[0]?.Id || null,
        TotalActivosVinculados: res.activos?.length || 1,
        ActivosVinculadosFolios: (res.activos || []).map((a: any) => a.Folio).join(', '),
      });
      mostrarExito(
        res.activos?.length > 1
          ? `${res.activos.length} activos creados y vinculados a la factura`
          : 'Activo nuevo creado y vinculado a la factura'
      );
      cerrarSubformRelacion();
    } catch (error: any) {
      mostrarError(error?.message || 'No se pudo crear y vincular el nuevo activo');
    } finally {
      setGuardandoRelacion(false);
    }
  };

  // ── Flujo manual ─────────────────────────────────────────────────────────

  const handleManualFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { id, value } = e.target;
    setManualForm(prev => ({ ...prev, [id]: value }));
  };

  const handleGuardarManual = async () => {
    if (!manualForm.folio.trim() || !manualForm.fecha || !manualForm.proveedorId) {
      mostrarError('Completa folio, fecha y proveedor'); return;
    }
    if (!manualPdfFile) {
      mostrarError('Adjunta el PDF de la factura'); return;
    }
    setGuardandoManual(true);
    try {
      const res = await facturasApi.crearManual(manualPdfFile, {
        folio: manualForm.folio,
        fecha: manualForm.fecha,
        proveedorId: manualForm.proveedorId,
        hotelId: hotelSeleccionadoId,
        usuarioCreadorId: usuario.Id,
      });
      if (!res || !res.facturaId) throw new Error('respuesta inválida');
      mostrarExito(`Factura ${res.folio || manualForm.folio} registrada correctamente`);
      handleVolverALista();
    } catch {
      mostrarError('No se pudo registrar la factura.');
    } finally {
      setGuardandoManual(false);
    }
  };

  // ── Resumen (paso 3 del flujo XML) ───────────────────────────────────────

  const resumenConteos = useMemo(() => {
    const conceptos = facturaGuardada?.conceptos || [];
    return {
      total: conceptos.length,
      nuevo: conceptos.filter((c: any) => c.EstatusRelacion === 'nuevo_activo').length,
      existente: conceptos.filter((c: any) => c.EstatusRelacion === 'activo_existente').length,
      omitido: conceptos.filter((c: any) => c.EstatusRelacion === 'omitido').length,
      pendiente: conceptos.filter((c: any) => !c.EstatusRelacion || c.EstatusRelacion === 'pendiente').length,
    };
  }, [facturaGuardada]);

  // ── Render: encabezado de formulario ─────────────────────────────────────

  const renderFormHeader = (titulo: string, subtitulo: string, accentColor: string) => (
    <Flex align="center" justify="space-between" px={6} py={5} borderBottom="1px solid" borderColor={borderColor}>
      <Box>
        <Text fontSize="lg" fontWeight="700" color={textPrimary}>{titulo}</Text>
        <Text {...labelStyle} mt={1}>{subtitulo}</Text>
      </Box>
      <Button size="sm" variant="outline" leftIcon={<Icon as={MdArrowBack} />} onClick={handleVolverALista}>
        Volver al listado
      </Button>
    </Flex>
  );

  // ── Render: vista lista ───────────────────────────────────────────────────

  const renderVistaLista = () => (
    <Box>
      <Card p="0px" overflow="hidden">
        <Flex align="center" justify="space-between" px={6} py={5} borderBottom="1px solid" borderColor={borderColor}>
          <Box>
            <Text fontSize="lg" fontWeight="700" color={textPrimary}>Facturas</Text>
            <Text {...labelStyle} mt={1}>{facturas.length} factura(s) registrada(s)</Text>
          </Box>
          <Button
            size="sm" colorScheme="blue" leftIcon={<Icon as={MdAdd} />}
            onClick={() => { setVistaActual('importar'); setTipoCarga(null); }}
          >
            Nueva factura
          </Button>
        </Flex>

        <CardBody px={0} py={0}>
          {loadingLista ? (
            <Center py={10}><Spinner /></Center>
          ) : facturas.length === 0 ? (
            <Center py={10}><Text color={textSecondary}>Aún no hay facturas registradas.</Text></Center>
          ) : (
            <TableContainer>
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>Folio</Th>
                    <Th>Proveedor</Th>
                    <Th>Fecha</Th>
                    <Th isNumeric>Total</Th>
                    <Th>Origen</Th>
                    <Th>Estatus</Th>
                    <Th>Acciones</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {facturas.map((f: any) => (
                    <Tr key={f.Id}>
                      <Td fontFamily="mono" fontWeight="600">{f.Folio}</Td>
                      <Td>{f.Proveedor || f.ProveedorNombre || '—'}</Td>
                      <Td>{formatFecha(f.Fecha)}</Td>
                      <Td isNumeric>{formatMoneda(f.Total, f.Moneda)}</Td>
                      <Td>
                        <Badge colorScheme={f.OrigenCarga === 'xml' ? 'cyan' : 'gray'} variant="subtle">
                          {f.OrigenCarga === 'xml' ? 'XML' : 'Manual'}
                        </Badge>
                      </Td>
                      <Td>
                        <Badge colorScheme={getEstatusFacturaColor(f.Estatus)} variant="subtle">
                          {f.Estatus}
                        </Badge>
                      </Td>
                      <Td>
                        <HStack spacing={1}>
                          <Button size="xs" variant="ghost" leftIcon={<Icon as={MdVisibility} />} onClick={() => handleVerFactura(f)}>
                            Ver
                          </Button>
                          <Button
                            size="xs" variant="ghost" leftIcon={<Icon as={MdPictureAsPdf} />}
                            onClick={() => window.open(facturasApi.descargarPdfUrl(f.Id), '_blank')}
                          >
                            PDF factura
                          </Button>
                          {f.OrigenCarga === 'xml' && (
                            <Button
                              size="xs" variant="ghost" leftIcon={<Icon as={MdCode} />}
                              onClick={() => window.open(facturasApi.descargarXmlUrl(f.Id), '_blank')}
                            >
                              XML
                            </Button>
                          )}
                          <Button
                            size="xs" variant="ghost" leftIcon={<Icon as={MdDescription} />}
                            onClick={() => window.open(facturasApi.descargarDetalleUrl(f.Id), '_blank')}
                          >
                            Reporte de vinculación
                          </Button>
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          )}
        </CardBody>
      </Card>
    </Box>
  );

  // ── Render: modal Ver factura ────────────────────────────────────────────

  const renderModalVer = () => (
    <Modal isOpen={isVerOpen} onClose={onCloseVer} size="3xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack spacing={3}>
            <Text>Factura {facturaViendo?.Folio}</Text>
            {facturaViendo?.Estatus && (
              <Badge colorScheme={getEstatusFacturaColor(facturaViendo.Estatus)} variant="subtle" fontSize="xs">
                {facturaViendo.Estatus}
              </Badge>
            )}
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {loadingVer ? (
            <Center py={8}><Spinner /></Center>
          ) : (
            <Box>
              <SimpleGrid columns={2} gap={3} mb={4}>
                <Box><Text {...labelStyle}>Proveedor</Text><Text fontSize="sm">{facturaViendo?.Proveedor || facturaViendo?.ProveedorNombre || '—'}</Text></Box>
                <Box><Text {...labelStyle}>Fecha</Text><Text fontSize="sm">{formatFecha(facturaViendo?.Fecha)}</Text></Box>
                <Box><Text {...labelStyle}>Moneda</Text><Text fontSize="sm">{facturaViendo?.Moneda || 'MXN'}</Text></Box>
                <Box><Text {...labelStyle}>Total</Text><Text fontSize="sm">{formatMoneda(facturaViendo?.Total, facturaViendo?.Moneda)}</Text></Box>
              </SimpleGrid>
              <Divider mb={4} />
              <Text {...labelStyle} mb={2}>Conceptos</Text>
              {(facturaViendo?.conceptos || []).length === 0 ? (
                <Text fontSize="sm" color={textSecondary}>Sin conceptos registrados.</Text>
              ) : (
                <TableContainer>
                  <Table size="sm">
                    <Thead>
                      <Tr>
                        <Th>Descripción</Th>
                        <Th isNumeric>Importe</Th>
                        <Th>Relación</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {(facturaViendo?.conceptos || []).map((c: any) => (
                        <Tr key={c.Id}>
                          <Td>{c.Descripcion}</Td>
                          <Td isNumeric>{formatMoneda(c.Importe, facturaViendo?.Moneda)}</Td>
                          <Td>
                            <Badge colorScheme={getEstatusRelacionColor(c.EstatusRelacion)} variant="subtle">
                              {getEstatusRelacionLabel(c.EstatusRelacion)}
                            </Badge>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </ModalBody>
        <ModalFooter>
          <Flex wrap="wrap" gap={2} justify="space-between" w="100%">
            <Flex wrap="wrap" gap={2}>
              <Button
                size="sm" leftIcon={<Icon as={MdPictureAsPdf} />} variant="outline"
                onClick={() => facturaViendo && window.open(facturasApi.descargarPdfUrl(facturaViendo.Id), '_blank')}
              >
                PDF de la factura
              </Button>
              {facturaViendo?.OrigenCarga === 'xml' && (
                <Button
                  size="sm" leftIcon={<Icon as={MdCode} />} variant="outline"
                  onClick={() => facturaViendo && window.open(facturasApi.descargarXmlUrl(facturaViendo.Id), '_blank')}
                >
                  XML
                </Button>
              )}
              <Button
                size="sm" leftIcon={<Icon as={MdDescription} />} variant="outline"
                onClick={() => facturaViendo && window.open(facturasApi.descargarDetalleUrl(facturaViendo.Id), '_blank')}
              >
                Reporte de vinculación
              </Button>
            </Flex>
            <Flex wrap="wrap" gap={2}>
              {facturaViendo?.Estatus !== 'cancelada' && (
                <Button
                  size="sm" leftIcon={<Icon as={MdBlock} />} colorScheme="red" variant="outline"
                  isLoading={cancelandoFactura}
                  onClick={handleCancelarFactura}
                >
                  Cancelar factura
                </Button>
              )}
              <Button size="sm" colorScheme="blue" onClick={onCloseVer}>Cerrar</Button>
            </Flex>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );

  // ── Render: elección de tipo de carga ────────────────────────────────────

  const renderEleccionTipoCarga = () => (
    <Card p="0px" overflow="hidden">
      {renderFormHeader('Nueva factura', 'Elige cómo quieres registrarla', 'blue.400')}
      <CardBody px={6} py={8}>
        <SimpleGrid columns={{ base: 1, md: 2 }} gap={5}>
          <Box
            as="button" textAlign="left" p={6} border="1px solid" borderColor={borderColor}
            borderRadius="14px" _hover={{ borderColor: 'blue.400', boxShadow: 'md' }} transition="all 0.15s"
            onClick={() => { setTipoCarga('xml'); setPasoActual(0); }}
          >
            <Icon as={MdCode} w="32px" h="32px" color="blue.400" mb={3} />
            <Text fontWeight="700" color={textPrimary} mb={1}>Importar XML (CFDI)</Text>
            <Text fontSize="sm" color={textSecondary}>
              Sube el XML de la factura para reconocer automáticamente proveedor, moneda y artículos.
            </Text>
          </Box>
          <Box
            as="button" textAlign="left" p={6} border="1px solid" borderColor={borderColor}
            borderRadius="14px" _hover={{ borderColor: 'blue.400', boxShadow: 'md' }} transition="all 0.15s"
            onClick={() => { setTipoCarga('manual'); setPasoActual(0); }}
          >
            <Icon as={MdReceiptLong} w="32px" h="32px" color="blue.400" mb={3} />
            <Text fontWeight="700" color={textPrimary} mb={1}>Registro manual</Text>
            <Text fontSize="sm" color={textSecondary}>
              Captura folio, fecha y proveedor, y adjunta el PDF de la factura.
            </Text>
          </Box>
        </SimpleGrid>
      </CardBody>
    </Card>
  );

  // ── Render: flujo XML — paso 0: Datos ────────────────────────────────────

  const renderPasoDatosXml = () => (
    <VStack align="stretch" spacing={5}>
      <FileFieldPicker
        label="Archivo XML (CFDI)" accept=".xml,text/xml" file={xmlFile} required
        borderColor={borderColor}
        onFileSelected={handleXmlSeleccionado}
        onClear={() => { setXmlFile(null); setParsedData(null); setParseError(null); setConceptosEditable([]); }}
      />

      {parsing && (
        <HStack><Spinner size="sm" /><Text fontSize="sm" color={textSecondary}>Leyendo el XML…</Text></HStack>
      )}

      {parseError && (
        <Alert status="warning" borderRadius="10px">
          <AlertIcon />
          <Box>
            <AlertDescription fontSize="sm">
              {parseError.error}. Puedes continuar con el registro manual adjuntando solo el PDF.
            </AlertDescription>
            <Button size="xs" mt={2} onClick={() => setTipoCarga('manual')}>
              Cambiar a registro manual
            </Button>
          </Box>
        </Alert>
      )}

      {parsedData && (
        <Box p={4} bg={sectionBg} borderRadius="12px">
          <SimpleGrid columns={{ base: 1, md: 3 }} gap={4}>
            <Box>
              <Text {...labelStyle}>Emisor</Text>
              <Text fontSize="sm" fontWeight="600">{parsedData.emisor?.nombre || '—'}</Text>
              <Text fontSize="xs" color={textSecondary}>RFC {parsedData.emisor?.rfc || '—'}</Text>
              {parsedData.proveedorSugerido ? (
                <Badge colorScheme="green" mt={1}>Proveedor existente</Badge>
              ) : (
                <Badge colorScheme="blue" mt={1}>Se creará un proveedor nuevo</Badge>
              )}
            </Box>
            <Box>
              <Text {...labelStyle}>Folio / Serie</Text>
              <Text fontSize="sm">{parsedData.serie || ''} {parsedData.folio || '—'}</Text>
            </Box>
            <Box>
              <Text {...labelStyle}>Moneda / Tipo de cambio</Text>
              <Text fontSize="sm">{parsedData.moneda} {parsedData.tipoCambio ? `· ${parsedData.tipoCambio}` : ''}</Text>
            </Box>
            <Box>
              <Text {...labelStyle}>Subtotal</Text>
              <Text fontSize="sm">{formatMoneda(parsedData.subTotal, parsedData.moneda)}</Text>
            </Box>
            <Box>
              <Text {...labelStyle}>Total</Text>
              <Text fontSize="sm" fontWeight="700">{formatMoneda(parsedData.total, parsedData.moneda)}</Text>
            </Box>
            <Box>
              <Text {...labelStyle}>Conceptos reconocidos</Text>
              <Text fontSize="sm">{parsedData.conceptos?.length || 0}</Text>
            </Box>
          </SimpleGrid>
        </Box>
      )}

      <FileFieldPicker
        label="Archivo PDF de la factura" accept="application/pdf" file={pdfFile} required
        borderColor={borderColor}
        onFileSelected={setPdfFile}
        onClear={() => setPdfFile(null)}
      />

      <Flex justify="flex-end">
        <Button colorScheme="blue" rightIcon={<Icon as={MdArrowForward} />} onClick={handleAvanzarDatosXml}>
          Siguiente
        </Button>
      </Flex>
    </VStack>
  );

  // ── Render: flujo XML — paso 1: Confirmar ────────────────────────────────

  const renderPasoConfirmarXml = () => (
    <VStack align="stretch" spacing={5}>
      <Text {...labelStyle}>Conceptos (editable antes de guardar)</Text>
      <TableContainer>
        <Table size="sm">
          <Thead>
            <Tr>
              <Th>Descripción</Th>
              <Th>Cantidad</Th>
              <Th>Valor unitario</Th>
              <Th>Importe</Th>
            </Tr>
          </Thead>
          <Tbody>
            {conceptosEditable.map((c, i) => (
              <Tr key={c._uid ?? i}>
                <Td minW="220px">
                  <Input size="sm" value={c.descripcion || ''} onChange={e => handleConceptoEditableChange(i, 'descripcion', e.target.value)} />
                </Td>
                <Td w="100px">
                  <Input size="sm" value={c.cantidad ?? ''} onChange={e => handleConceptoEditableChange(i, 'cantidad', e.target.value)} />
                </Td>
                <Td w="140px">
                  <Input size="sm" value={c.valorUnitario ?? ''} onChange={e => handleConceptoEditableChange(i, 'valorUnitario', e.target.value)} />
                </Td>
                <Td w="140px">
                  <Input size="sm" value={c.importe ?? ''} onChange={e => handleConceptoEditableChange(i, 'importe', e.target.value)} />
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>

      <Flex justify="space-between">
        <Button variant="outline" leftIcon={<Icon as={MdArrowBack} />} onClick={() => setPasoActual(0)}>
          Atrás
        </Button>
        <Button colorScheme="blue" isLoading={guardandoFactura} onClick={handleGuardarFacturaXml}>
          Guardar factura
        </Button>
      </Flex>
    </VStack>
  );

  // ── Render: subformulario "nuevo activo" ─────────────────────────────────

  const renderSubformNuevoActivo = (concepto: any) => (
    <Box mt={3} p={4} bg={sectionBg} borderRadius="12px">
      <Flex justify="space-between" align="center" mb={3} wrap="wrap" gap={2}>
        <Text fontWeight="600" fontSize="sm">Datos del nuevo activo</Text>
        {nuevoActivoForm.FamiliaId && (
          folioPreview ? (
            <Badge colorScheme="blue" fontSize="xs" px={2} py={1} borderRadius="6px" fontFamily="mono">
              {seriesNuevoActivo.length > 1
                ? `Folios que se asignarán: ${folioPreview} → ${calcularUltimoFolioPreview(folioPreview, seriesNuevoActivo.length)}`
                : `Folio que se asignará: ${folioPreview}`}
            </Badge>
          ) : folioPreviewError ? (
            <Badge colorScheme="red" fontSize="xs" px={2} py={1} borderRadius="6px">
              {folioPreviewError}
            </Badge>
          ) : null
        )}
      </Flex>
      <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
        <InputField
          id="ActiveName" label="Nombre" mb="0px"
          value={nuevoActivoForm.ActiveName || ''}
          onChange={(e: any) => setNuevoActivoForm((p: any) => ({ ...p, ActiveName: e.target.value }))}
        />
        <InputField
          id="Precio" label="Precio" type="number" mb="0px"
          value={nuevoActivoForm.Precio || ''}
          onChange={(e: any) => setNuevoActivoForm((p: any) => ({ ...p, Precio: e.target.value }))}
        />
        <PickList
          id="LineaId" label="Línea" required mb="0px"
          placeholder="Selecciona una línea"
          options={lineas.map((l: any) => ({ value: String(l.id_linea), label: l.nom_linea }))}
          value={nuevoActivoForm.LineaId || ''}
          onChange={(e: any) => handleNuevoActivoChange('LineaId', e.target.value)}
        />
        <PickList
          id="FamiliaId" label="Sublínea" required mb="0px"
          placeholder={nuevoActivoForm.LineaId ? 'Selecciona una sublínea' : 'Primero selecciona una línea'}
          isDisabled={!nuevoActivoForm.LineaId}
          options={familiasFiltradas.map((f: any) => ({ value: String(f.id_sub), label: f.Name }))}
          value={nuevoActivoForm.FamiliaId || ''}
          onChange={(e: any) => handleNuevoActivoChange('FamiliaId', e.target.value)}
        />
        <PickList
          id="MarcaId" label="Marca" required mb="0px"
          placeholder="Selecciona una marca"
          options={marcas.map((m: any) => ({ value: String(m.Id), label: m.Name }))}
          value={nuevoActivoForm.MarcaId || ''}
          onChange={(e: any) => handleNuevoActivoChange('MarcaId', e.target.value)}
        />
        <PickList
          id="ModeloId" label="Modelo" required mb="0px"
          placeholder="Selecciona un modelo"
          options={modelos.map((m: any) => ({ value: String(m.Id), label: m.Name }))}
          value={nuevoActivoForm.ModeloId || ''}
          onChange={(e: any) => handleNuevoActivoChange('ModeloId', e.target.value)}
        />
        {seriesNuevoActivo.length <= 1 ? (
          <InputField
            id="SerialNumber" label="Número de serie" required mb="0px"
            value={seriesNuevoActivo[0] || ''}
            onChange={(e: any) => setSeriesNuevoActivo([e.target.value])}
          />
        ) : (
          <Box gridColumn="1 / -1">
            <Text fontSize="sm" fontWeight="600" mb={2}>
              Esta línea trae {seriesNuevoActivo.length} unidades — captura el número de serie de cada una
            </Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
              {seriesNuevoActivo.map((serie, i) => (
                <InputField
                  key={i}
                  id={`SerialNumber-${i}`} label={`Serie unidad ${i + 1} de ${seriesNuevoActivo.length}`} required mb="0px"
                  value={serie}
                  onChange={(e: any) => setSeriesNuevoActivo((prev) => {
                    const next = [...prev];
                    next[i] = e.target.value;
                    return next;
                  })}
                />
              ))}
            </SimpleGrid>
          </Box>
        )}
        <PickList
          id="DepartamentoId" label="Departamento" required mb="0px"
          placeholder="Selecciona un departamento"
          options={departamentosCatalogo.map((d: any) => ({ value: String(d.Id), label: d.Name }))}
          value={nuevoActivoForm.DepartamentoId || ''}
          onChange={(e: any) => handleNuevoActivoChange('DepartamentoId', e.target.value)}
        />
        <PickList
          id="TipoAdquisicionId" label="Tipo de adquisición" required mb="0px"
          placeholder="Capex / OPEX / Arrendamiento"
          options={tiposAdquisicion.map((t: any) => ({ value: String(t.Id), label: t.Name }))}
          value={nuevoActivoForm.TipoAdquisicionId || ''}
          onChange={(e: any) => handleNuevoActivoChange('TipoAdquisicionId', e.target.value)}
        />
        {tipoEsArrendamiento && (
          <SelectField
            id="ArrendadoraId" label="Arrendadora" required mb="0px"
            placeholder="Selecciona o escribe para crear una arrendadora"
            isLoading={isCreatingArrendadora}
            creatable
            onCreateOption={handleCreateArrendadora}
            options={arrendadoras.map((a: any) => ({ value: String(a.Id), label: a.Name }))}
            value={nuevoActivoForm.ArrendadoraId || ''}
            onChange={(value: string) => handleNuevoActivoChange('ArrendadoraId', value)}
          />
        )}
        <InputField
          id="FechaAdquisicion" label="Fecha de adquisición" type="date" mb="0px"
          value={nuevoActivoForm.FechaAdquisicion || ''}
          onChange={(e: any) => setNuevoActivoForm((p: any) => ({ ...p, FechaAdquisicion: e.target.value }))}
        />
        <PickList
          id="CategoriaDepreciacionId"
          label={`Rubro de depreciación (tabla ${nuevoActivoForm.FechaAdquisicion ? nuevoActivoForm.FechaAdquisicion.split('-')[0] : new Date().getFullYear()})`}
          mb="0px"
          isDisabled={rubrosDepreciacion.length === 0}
          placeholder={rubrosDepreciacion.length === 0 ? 'No hay tabla de depreciación para ese año' : 'Selecciona el rubro contable (opcional)'}
          options={rubrosDepreciacion.map((r: any) => ({ value: String(r.Id), label: `${r.Categoria} (${r.Porcentaje}% anual)` }))}
          value={nuevoActivoForm.CategoriaDepreciacionId || ''}
          onChange={(e: any) => handleNuevoActivoChange('CategoriaDepreciacionId', e.target.value)}
        />
        <InputField
          id="Garantia" label="Garantía (fecha de vencimiento)" type="date" mb="0px"
          value={nuevoActivoForm.Garantia || ''}
          onChange={(e: any) => setNuevoActivoForm((p: any) => ({ ...p, Garantia: e.target.value }))}
        />
        <InputField
          id="UbicacionExacta" label="Ubicación exacta" mb="0px"
          value={nuevoActivoForm.UbicacionExacta || ''}
          onChange={(e: any) => setNuevoActivoForm((p: any) => ({ ...p, UbicacionExacta: e.target.value }))}
        />
      </SimpleGrid>

      <Box mt={4}>
        <FileFieldPicker
          label="Imagen del activo (opcional)" accept="image/*" file={nuevoActivoImagen}
          borderColor={borderColor}
          onFileSelected={setNuevoActivoImagen}
          onClear={() => setNuevoActivoImagen(null)}
        />
      </Box>

      <Flex justify="flex-end" mt={4} gap={2}>
        <Button variant="ghost" onClick={cerrarSubformRelacion}>Cancelar</Button>
        <Button colorScheme="blue" isLoading={guardandoRelacion} onClick={() => handleGuardarNuevoActivo(concepto)}>
          Crear y vincular activo
        </Button>
      </Flex>
    </Box>
  );

  // ── Render: subformulario "activo existente" ─────────────────────────────

  const renderSubformExistente = (concepto: any) => (
    <Box mt={3} p={4} bg={sectionBg} borderRadius="12px">
      <Text fontWeight="600" fontSize="sm" mb={3}>Buscar activo existente</Text>
      <RadioGroup value={busquedaTipo} onChange={(v: any) => { setBusquedaTipo(v); setResultadosBusqueda([]); setBusquedaRealizada(false); }} mb={3}>
        <ChakraStack direction="row" spacing={5}>
          <Radio value="serial">Por número de serie</Radio>
          <Radio value="marca_modelo">Por marca / modelo</Radio>
        </ChakraStack>
      </RadioGroup>

      {busquedaTipo === 'serial' ? (
        <Input
          size="sm" placeholder="Número de serie exacto" value={busquedaSerial}
          onChange={e => setBusquedaSerial(e.target.value)} maxW="320px"
        />
      ) : (
        <SimpleGrid columns={2} gap={3} maxW="480px">
          <PickList
            id="busquedaMarcaId" placeholder="Marca (opcional)" mb="0px"
            options={marcas.map((m: any) => ({ value: String(m.Id), label: m.Name }))}
            value={busquedaMarcaId} onChange={(e: any) => setBusquedaMarcaId(e.target.value)}
          />
          <PickList
            id="busquedaModeloId" placeholder="Modelo (opcional)" mb="0px"
            options={modelos.map((m: any) => ({ value: String(m.Id), label: m.Name }))}
            value={busquedaModeloId} onChange={(e: any) => setBusquedaModeloId(e.target.value)}
          />
        </SimpleGrid>
      )}

      <Button size="sm" mt={3} leftIcon={<Icon as={MdSearch} />} isLoading={buscando} onClick={handleBuscarActivos}>
        Buscar
      </Button>

      {busquedaRealizada && !buscando && (
        resultadosBusqueda.length === 0 ? (
          <Text fontSize="sm" color={textSecondary} mt={3}>No se encontraron activos con esos criterios.</Text>
        ) : (
          <TableContainer mt={3}>
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>Folio</Th>
                  <Th>Nombre</Th>
                  <Th>Serie</Th>
                  <Th></Th>
                </Tr>
              </Thead>
              <Tbody>
                {resultadosBusqueda.map((a: any) => (
                  <Tr key={a.Id}>
                    <Td fontFamily="mono">{a.Folio}</Td>
                    <Td>
                      {a.ActiveName}
                      {a.FacturaId ? (
                        <HStack spacing={1} mt={1}>
                          <Icon as={MdWarning} color="orange.400" w="12px" h="12px" />
                          <Text fontSize="xs" color="orange.500">Ya vinculado a factura {a.FacturaFolio || a.FacturaId}</Text>
                        </HStack>
                      ) : null}
                    </Td>
                    <Td fontFamily="mono">{a.SerialNumber}</Td>
                    <Td>
                      <Button size="xs" colorScheme="purple" leftIcon={<Icon as={MdLink} />} isLoading={guardandoRelacion}
                        onClick={() => handleVincularExistente(concepto, a)}>
                        Vincular
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        )
      )}

      <Flex justify="flex-end" mt={3}>
        <Button variant="ghost" onClick={cerrarSubformRelacion}>Cancelar</Button>
      </Flex>
    </Box>
  );

  // ── Render: flujo XML — paso 2: Relacionar artículos ─────────────────────

  const renderPasoRelacionar = () => {
    if (!facturaGuardada) return null;
    return (
      <VStack align="stretch" spacing={4}>
        <Text {...labelStyle}>Relaciona cada artículo con un activo (o guarda sin relacionar y hazlo después)</Text>
        {facturaGuardada.conceptos.map((c: any) => {
          const enProceso = conceptoEnProceso === c.Id;
          const estatus = c.EstatusRelacion || 'pendiente';
          return (
            <Box key={c.Id} p={4} border="1px solid" borderColor={borderColor} borderRadius="12px" bg={cardBg}>
              <Flex justify="space-between" align="start" wrap="wrap" gap={2}>
                <Box flex={1}>
                  <Text fontWeight="600" fontSize="sm">{c.Descripcion}</Text>
                  <Text fontSize="xs" color={textSecondary}>
                    {formatMoneda(c.Importe, facturaGuardada && (facturaGuardada as any).moneda)}
                    {c.Cantidad != null ? ` · Cantidad: ${c.Cantidad}` : ''}
                  </Text>
                  {(c.TotalActivosVinculados || 0) > 1 && (
                    <Text fontSize="xs" color={textSecondary} mt={1}>
                      {c.TotalActivosVinculados} activos vinculados: {c.ActivosVinculadosFolios}
                    </Text>
                  )}
                </Box>
                <Badge colorScheme={getEstatusRelacionColor(estatus)}>{getEstatusRelacionLabel(estatus)}</Badge>
              </Flex>

              {estatus === 'pendiente' && !enProceso && (
                <HStack spacing={2} mt={3}>
                  <Button size="xs" colorScheme="blue" leftIcon={<Icon as={MdAdd} />} onClick={() => handleAbrirNuevoActivo(c)}>
                    Nuevo activo
                  </Button>
                  <Button size="xs" colorScheme="purple" leftIcon={<Icon as={MdSearch} />} onClick={() => handleAbrirExistente(c)}>
                    Activo existente
                  </Button>
                  <Button size="xs" variant="ghost" leftIcon={<Icon as={MdBlock} />} onClick={() => handleOmitirConcepto(c)}>
                    Omitir
                  </Button>
                </HStack>
              )}

              {enProceso && modoRelacion === 'nuevo' && renderSubformNuevoActivo(c)}
              {enProceso && modoRelacion === 'existente' && renderSubformExistente(c)}
            </Box>
          );
        })}

        <Flex justify="flex-end">
          <Button colorScheme="blue" rightIcon={<Icon as={MdArrowForward} />} onClick={() => setPasoActual(3)}>
            Continuar al resumen
          </Button>
        </Flex>
      </VStack>
    );
  };

  // ── Render: flujo XML — paso 3: Resumen ──────────────────────────────────

  const renderPasoResumen = () => (
    <VStack align="stretch" spacing={5}>
      <SimpleGrid columns={{ base: 2, md: 4 }} gap={4}>
        <Box p={4} bg={sectionBg} borderRadius="12px" textAlign="center">
          <Text fontSize="2xl" fontWeight="700">{resumenConteos.total}</Text>
          <Text {...labelStyle}>Conceptos</Text>
        </Box>
        <Box p={4} bg={sectionBg} borderRadius="12px" textAlign="center">
          <Text fontSize="2xl" fontWeight="700" color="blue.500">{resumenConteos.nuevo}</Text>
          <Text {...labelStyle}>Nuevo activo</Text>
        </Box>
        <Box p={4} bg={sectionBg} borderRadius="12px" textAlign="center">
          <Text fontSize="2xl" fontWeight="700" color="purple.500">{resumenConteos.existente}</Text>
          <Text {...labelStyle}>Activo existente</Text>
        </Box>
        <Box p={4} bg={sectionBg} borderRadius="12px" textAlign="center">
          <Text fontSize="2xl" fontWeight="700" color="orange.500">{resumenConteos.pendiente}</Text>
          <Text {...labelStyle}>Pendientes</Text>
        </Box>
      </SimpleGrid>

      <Alert status="success" borderRadius="10px">
        <AlertIcon />
        <AlertDescription fontSize="sm">
          Factura {facturaGuardada?.folio} guardada. Los conceptos pendientes se pueden relacionar más tarde
          desde la edición de cada activo.
        </AlertDescription>
      </Alert>

      <HStack spacing={3}>
        <Button
          leftIcon={<Icon as={MdPictureAsPdf} />} variant="outline"
          onClick={() => facturaGuardada && window.open(facturasApi.descargarPdfUrl(facturaGuardada.facturaId), '_blank')}
        >
          Descargar PDF de la factura
        </Button>
        <Button
          leftIcon={<Icon as={MdCode} />} variant="outline"
          onClick={() => facturaGuardada && window.open(facturasApi.descargarXmlUrl(facturaGuardada.facturaId), '_blank')}
        >
          Descargar XML
        </Button>
        <Button
          leftIcon={<Icon as={MdDescription} />} variant="outline"
          onClick={() => facturaGuardada && window.open(facturasApi.descargarDetalleUrl(facturaGuardada.facturaId), '_blank')}
        >
          Descargar reporte de vinculación
        </Button>
      </HStack>

      <Flex justify="flex-end">
        <Button colorScheme="blue" leftIcon={<Icon as={MdCheckCircle} />} onClick={handleVolverALista}>
          Volver al listado
        </Button>
      </Flex>
    </VStack>
  );

  // ── Render: flujo manual ─────────────────────────────────────────────────

  const renderFlujoManual = () => (
    <Card p="0px" overflow="hidden">
      {renderFormHeader('Registro manual de factura', 'Folio, fecha, proveedor y PDF', 'blue.400')}
      <CardBody px={6} py={6}>
        <VStack align="stretch" spacing={5}>
          <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
            <InputField
              id="folio" label="Folio" required mb="0px"
              value={manualForm.folio} onChange={handleManualFormChange}
            />
            <InputField
              id="fecha" label="Fecha" type="date" required mb="0px"
              value={manualForm.fecha} onChange={handleManualFormChange}
            />
            <PickList
              id="proveedorId" label="Proveedor" required mb="0px"
              placeholder="Selecciona un proveedor"
              options={proveedores.map((p: any) => ({ value: String(p.Id), label: p.Proveedor }))}
              value={manualForm.proveedorId} onChange={handleManualFormChange}
            />
          </SimpleGrid>

          <FileFieldPicker
            label="Archivo PDF de la factura" accept="application/pdf" file={manualPdfFile} required
            borderColor={borderColor}
            onFileSelected={setManualPdfFile}
            onClear={() => setManualPdfFile(null)}
          />

          <Flex justify="flex-end">
            <Button colorScheme="blue" isLoading={guardandoManual} onClick={handleGuardarManual}>
              Registrar factura
            </Button>
          </Flex>
        </VStack>
      </CardBody>
    </Card>
  );

  // ── Render: flujo XML (stepper) ──────────────────────────────────────────

  const renderFlujoXml = () => (
    <Card p="0px" overflow="hidden">
      {renderFormHeader('Importar factura desde XML', 'CFDI 3.3 / 4.0', 'blue.400')}
      <Box px={6} py={4} borderBottom="1px solid" borderColor={borderColor}>
        <Stepper index={pasoActual} colorScheme="blue" size="sm">
          {pasosXml.map((paso, i) => (
            <Step key={i}>
              <StepIndicator>
                <StepStatus complete={<StepIcon />} incomplete={<StepNumber />} active={<StepNumber />} />
              </StepIndicator>
              <Box flexShrink={0}><StepTitle>{paso}</StepTitle></Box>
              <StepSeparator />
            </Step>
          ))}
        </Stepper>
      </Box>
      <CardBody px={6} py={6}>
        {pasoActual === 0 && renderPasoDatosXml()}
        {pasoActual === 1 && renderPasoConfirmarXml()}
        {pasoActual === 2 && renderPasoRelacionar()}
        {pasoActual === 3 && renderPasoResumen()}
      </CardBody>
    </Card>
  );

  const renderVistaImportar = () => {
    if (!tipoCarga) return renderEleccionTipoCarga();
    return tipoCarga === 'xml' ? renderFlujoXml() : renderFlujoManual();
  };

  // ── Render principal ──────────────────────────────────────────────────────

  const viewVariants = {
    initial: { opacity: 0, y: 18 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.28, ease: [0.22, 1, 0.36, 1] } },
    exit: { opacity: 0, y: -10, transition: { duration: 0.18, ease: 'easeIn' } },
  };

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }} px={{ base: 4, md: 6 }} overflow="hidden">
      <AnimatePresence mode="wait">
        {vistaActual === 'lista' && (
          <motion.div key="lista" variants={viewVariants} initial="initial" animate="animate" exit="exit">
            {renderVistaLista()}
          </motion.div>
        )}
        {vistaActual === 'importar' && (
          <motion.div key="importar" variants={viewVariants} initial="initial" animate="animate" exit="exit">
            {renderVistaImportar()}
          </motion.div>
        )}
      </AnimatePresence>
      {renderModalVer()}
    </Box>
  );
};

export default FacturasPage;
